import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Input,
  FormGroup,
  Card,
  CardBody,
} from "reactstrap"; // Make sure you have CustomInput from reactstrap for switch
import { FormControlLabel, Switch } from "@mui/material";
import { useSpaceStore } from "../../../../stores/Assets/space";
import { useUsageStore } from "../../../../stores/Usage";
import { SketchPicker } from "react-color";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useMenuStore } from "../../../../stores/Assets/menu";
import { useKitchenStore } from "../../../../stores/Assets/kitchen";
import { Permission } from "../../../../Layouts/Permissions";
import Dragula from "react-dragula";
import "dragula/dist/dragula.css";
import { useAuth } from "../../../../stores";
import {
  canFullOrder,
  getDefaultServiceLevel,
  getMaxServiceLevel,
  getServiceLevelOptions,
} from "../../../../helpers/planLimits";

const validTypes = ["OFFICE", "MAJLISS", "RESTAURANT", "RESTAURANT_2", "BEACH"];
const languages = [
  {
    label: "ENGLISH",
    value: "EN",
  },
  {
    label: "ARABIC",
    value: "AR",
  },
];

const AddSpace = ({
  kitchenList,
  menuList,
  siteList,
  showAddSpaceModal,
  toggleAddSpaceModal,
}) => {
  //selected states
  const [selectedSiteId, setSelectedSiteId] = useState(
    siteList[0]?.site_id || null,
  );
  const [selectedKichenId, setSelectedKichenId] = useState(
    kitchenList[0]?.kitchen_id || null,
  );
  const [selectedSecondaryKitchenId, setSelectedSecondaryKitchenId] = useState("");
  const [selectedMenuIds, setSelectedMenuIds] = useState(
    menuList?.length > 0 ? [parseInt(menuList[0]?.menu_id)] : [],
  );
  const [serviceLevelOptions, setServiceLevelOptions] = useState(
    getServiceLevelOptions(3),
  );
  // states
  const [loading, setLoading] = useState(false);
  const [theme1, setTheme1] = useState("");
  const [theme2, setTheme2] = useState("");
  const [saveLastOrder, setSaveLastOrder] = useState(false);
  const [call_active, setCallActive] = useState(false);
  const [serviceLevel, setServiceLevel] = useState(0);
  const [useCustomTheme, setUseCustomTheme] = useState(false); // New state to control the switch
  const [showFeedback, setShowFeedback] = useState(true);

  //stores
  const { createSpace, isLoading } = useSpaceStore((state) => state);
  const { getMenus, menus } = useMenuStore((state) => state);
  const { getKitchensBySite, kitchens } = useKitchenStore((state) => state);
  const { usage, fetchUsage } = useUsageStore();
  const user = useAuth((state) => state.user);
  // Drag and drop ref
  const dragulaContainerRef = useRef(null);

  useEffect(() => {
    // Fetch usage to determine service level options
    fetchUsage();
    // Dynamically set service level options based on usage limits
  }, [fetchUsage]);
  useEffect(() => {
    const maxLevel = user?.role === "ROOT" ? 3 : getMaxServiceLevel(usage);
    setServiceLevelOptions(getServiceLevelOptions(maxLevel));
  }, [usage, user]);
  // Fetch menus and kitchens when the selected site changes
  useEffect(() => {
    let isMounted = true;

    const fetchSiteData = async () => {
      if (!selectedSiteId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setSelectedMenuIds([]);
      try {
        await Promise.all([
          getMenus(selectedSiteId, 1, 50),
          getKitchensBySite(selectedSiteId, { page: 1, perPage: 500 }),
        ]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSiteData();

    return () => {
      isMounted = false;
    };
  }, [selectedSiteId, getMenus, getKitchensBySite]);
  // select the first menu and kitchen when the site is selected
  useEffect(() => {
    if (selectedSiteId && menus?.length > 0) {
      // Preselect all menus in the current site ordering from backend
      setSelectedMenuIds([]);
      setSelectedMenuIds(menus.map((m) => parseInt(m.menu_id)));
    }
  }, [selectedSiteId, menus]);

  useEffect(() => {
    if (selectedSiteId && kitchens?.length > 0) {
      setSelectedKichenId(kitchens[0]?.kitchen_id || null);
      setSelectedSecondaryKitchenId("");
    }
  }, [selectedSiteId, kitchens]);

  // Initialize Dragula for drag and drop
  useEffect(() => {
    if (dragulaContainerRef.current) {
      const drake = Dragula([dragulaContainerRef.current], {
        moves: function (el, container, handle) {
          return true;
        },
      });

      drake.on("drop", function (el, target, source, sibling) {
        // Get the new order of menu IDs from the DOM
        const newOrder = Array.from(dragulaContainerRef.current.children).map(
          (child) => parseInt(child.getAttribute("data-menu-id")),
        );
        setSelectedMenuIds(newOrder);
      });

      return () => {
        drake.destroy();
      };
    }
  }, [selectedMenuIds.length]);

  const formik = useFormik({
    initialValues: {
      name: "",
      name_ar: "",
      // call_active: false,
      default_lang: languages[0].value,
      type: validTypes[0],
      theme: "",
      service_level: getDefaultServiceLevel(usage),
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
      name_ar: Yup.string().optional(),
      // call_active: Yup.boolean().optional(),
      service_level: Yup.number().optional(),
      default_lang: Yup.string().required(t("Required")),
      theme: Yup.string()
        .matches(/^#[0-9A-Fa-f]{6}$/, t("Invalid hex color"))
        .optional(),
      type: Yup.string()
        .oneOf(validTypes, t("Invalid type"))
        .required(t("Required")),
    }),
    onSubmit: async (values) => {
      let theme = "";
      if (!useCustomTheme) {
        theme = ""; // No theme if custom theme is not enabled
      } else if (theme1 && !theme2) {
        theme = theme1;
      } else if (!theme1 && theme2) {
        theme = theme2;
      } else {
        theme = `${theme1},${theme2}`;
      }

      const spaceData = {
        name: values.name,
        name_ar: values.name_ar,
        default_lang: values.default_lang,
        type: values.type,
        theme: theme,
        saveLastOrder: saveLastOrder,
        // call_active: call_active,
        showFeedback: showFeedback,
        service_level: parseInt(values.service_level),
        secondary_kitchen_id: selectedSecondaryKitchenId
          ? parseInt(selectedSecondaryKitchenId)
          : null,
      };

      // Call the createSpace function with the selected site, kitchen, and menu IDs
      // Filter out null values from selectedMenuIds before sending
      const filteredMenuIds = selectedMenuIds.filter(
        (id) => id !== null && id !== undefined,
      );
      const result = await createSpace(
        parseInt(selectedSiteId),
        spaceData,
        parseInt(selectedKichenId),
        filteredMenuIds.map((id) => parseInt(id)),
      );
      if (result) {
        useUsageStore.getState().fetchUsage();
        formik.resetForm();
        setSaveLastOrder(false); // Reset to false
        setShowFeedback(true);
        setSelectedSecondaryKitchenId("");
        setSelectedSiteId(siteList[0]?.site_id || null); // Reset to first site after submission
        toggleAddSpaceModal();
      }
    },
  });

  return (
    <Modal isOpen={showAddSpaceModal} toggle={toggleAddSpaceModal}>
      <ModalHeader toggle={toggleAddSpaceModal}>
        {t("Add New Space")}
      </ModalHeader>
      <ModalBody>
        {siteList.length === 0 ? (
          <div className="text-center">
            <p className="text-danger">{t("You should first add a site.")}</p>
            <Link to="/sites" className="btn btn-primary">
              {t("Go to Sites")}
            </Link>
          </div>
        ) : (
          <form
            onSubmit={formik.handleSubmit}
            className="d-flex flex-column flex-wrap"
          >
            <div className="flex-fill mb-2">
              <Label for="site">{t("Select Site")}</Label>
              <select
                id="site"
                onChange={(e) => setSelectedSiteId(e.target.value)}
                value={selectedSiteId}
                className="form-control"
              >
                <option value="">Select</option>
                {siteList.map((site) => (
                  <option key={site.site_id} value={site.site_id}>
                    {site.site_id} - {site.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedSiteId && (
              <>
                <div className="flex-fill mb-2">
                  <Label for="Kitchen">{t("Select Kitchen")}</Label>
                  <select
                    id="Kitchen"
                    onChange={(e) => {
                      const nextKitchenId = e.target.value;
                      setSelectedKichenId(nextKitchenId);
                      if (String(selectedSecondaryKitchenId) === String(nextKitchenId)) {
                        setSelectedSecondaryKitchenId("");
                      }
                    }}
                    disabled={loading}
                    value={selectedKichenId}
                    className="form-control"
                  >
                    {kitchens?.length > 0 &&
                      kitchens.map((kitchen) => (
                        <option
                          key={kitchen.kitchen_id}
                          value={kitchen.kitchen_id}
                        >
                          {kitchen.kitchen_id} - {kitchen.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex-fill mb-2">
                  <Label for="SecondaryKitchen">{t("Secondary Kitchen")}</Label>
                  <select
                    id="SecondaryKitchen"
                    onChange={(e) => setSelectedSecondaryKitchenId(e.target.value)}
                    disabled={loading}
                    value={selectedSecondaryKitchenId}
                    className="form-control"
                  >
                    <option value="">{t("None")}</option>
                    {kitchens
                      ?.filter(
                        (kitchen) =>
                          String(kitchen.kitchen_id) !==
                          String(selectedKichenId),
                      )
                      .map((kitchen) => (
                        <option
                          key={kitchen.kitchen_id}
                          value={kitchen.kitchen_id}
                        >
                          {kitchen.kitchen_id} - {kitchen.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex-fill mb-2">
                  <Label for="Menu">{t("Select Menu")}</Label>
                  <Select
                    id="Menu"
                    isMulti
                    isDisabled={loading}
                    isLoading={loading}
                    options={
                      menus?.length > 0 &&
                      menus.map((menu) => ({
                        value: parseInt(menu.menu_id),
                        label: `${menu.menu_id} - ${menu.name} ${menu.alias ? `(${menu.alias})` : ""}`,
                      }))
                    }
                    value={selectedMenuIds
                      .map((id) =>
                        menus?.find((menu) => parseInt(menu.menu_id) === id),
                      )
                      .filter(Boolean)
                      .map((menu) => ({
                        value: parseInt(menu.menu_id),
                        label: `${menu.menu_id} - ${menu.name} ${menu.alias ? `(${menu.alias})` : ""}`,
                      }))}
                    onChange={(selectedOptions) => {
                      if (selectedOptions) {
                        const nextIds = selectedOptions
                          .map((option) => parseInt(option.value))
                          .filter((id) => !isNaN(id) && id !== null);
                        const kept = selectedMenuIds.filter((id) =>
                          nextIds.includes(id),
                        );
                        const added = nextIds.filter(
                          (id) => !selectedMenuIds.includes(id),
                        );
                        setSelectedMenuIds([...kept, ...added]);
                      } else {
                        setSelectedMenuIds([]);
                      }
                    }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                </div>

                {selectedMenuIds.length > 0 && (
                  <div className="flex-fill mb-2">
                    <Label>{t("Reorder Menus (Drag to Reorder)")}</Label>
                    <div
                      ref={dragulaContainerRef}
                      className="menu-order-container"
                      style={{
                        minHeight: "50px",
                        padding: "10px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "4px",
                        border: "1px dashed #dee2e6",
                      }}
                    >
                      {selectedMenuIds.map((menuId) => {
                        const menu = menus?.find(
                          (m) => parseInt(m.menu_id) === menuId,
                        );
                        return menu ? (
                          <Card
                            key={menuId}
                            data-menu-id={menuId}
                            className="mb-2"
                            style={{
                              cursor: "move",
                              userSelect: "none",
                            }}
                          >
                            <CardBody className="p-2 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <i className="ri-drag-move-fill me-2 text-muted"></i>
                                <span>
                                  {menu.menu_id} - {menu.name}{" "}
                                  {menu.alias ? `(${menu.alias})` : ""}
                                </span>
                              </div>
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => {
                                  setSelectedMenuIds(
                                    selectedMenuIds.filter(
                                      (id) => id !== menuId,
                                    ),
                                  );
                                }}
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            </CardBody>
                          </Card>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex-fill mb-2">
              <Label for="name">{t("Name")}</Label>
              <input
                type="text"
                id="name"
                name="name"
                onChange={formik.handleChange}
                value={formik.values.name}
                className="form-control"
              />
              {formik.errors.name && (
                <div className="text-danger">{formik.errors.name}</div>
              )}
            </div>
            <div className="flex-fill mb-2">
              <Label for="name_ar">{t("Name (AR)")}</Label>
              <input
                type="text"
                id="name_ar"
                name="name_ar"
                onChange={formik.handleChange}
                value={formik.values.name_ar}
                className="form-control"
              />
              {formik.errors.name_ar && (
                <div className="text-danger">{formik.errors.name_ar}</div>
              )}
            </div>
            <div className="flex-fill mb-2">
              <Label for="default_lang">{t("Default Language")}</Label>
              <select
                id="default_lang"
                name="default_lang"
                onChange={formik.handleChange}
                value={formik.values.default_lang}
                className="form-control"
              >
                {languages.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    label={type.label}
                  />
                ))}
              </select>
              {formik.errors.default_lang && (
                <div className="text-danger">{formik.errors.default_lang}</div>
              )}
            </div>

            <div className="flex-fill mb-2">
              <Label for="type">{t("Type")}</Label>
              <select
                id="type"
                name="type"
                onChange={formik.handleChange}
                value={formik.values.type}
                className="form-control"
              >
                {validTypes.map((type) => (
                  <option key={type} value={type} label={type} />
                ))}
              </select>
              {formik.errors.type && (
                <div className="text-danger">{formik.errors.type}</div>
              )}
            </div>
            {/**if user is ROOT he can see the input of  service level */}
            <div className="flex-fill mb-2">
              <Label for="service_level">{t("Service Level")}</Label>
              <select
                id="service_level"
                name="service_level"
                onChange={formik.handleChange}
                value={formik.values.service_level}
                className="form-control"
              >
                {serviceLevelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>

            {canFullOrder(usage) && (
              <div className="flex-fill mb-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={saveLastOrder}
                      onChange={(event) =>
                        setSaveLastOrder(event.target.checked)
                      }
                    />
                  }
                  label={t("Save Last Order")}
                />
              </div>
            )}
            {/* {useUsageStore.getState().usage?.limits?.can_splash && (
              <div className="flex-fill mb-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={call_active}
                      onChange={(event) => setCallActive(event.target.checked)}
                    />
                  }
                  label={t("Call Teaboy")}
                />
              </div>
            )} */}
            <div className="flex-fill mb-2">
              <FormControlLabel
                control={
                  <Switch
                    checked={showFeedback}
                    onChange={(event) => setShowFeedback(event.target.checked)}
                  />
                }
                label={t("Show Feedback section")}
              />
            </div>

            <div className="flex-fill mb-2">
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomTheme}
                    onChange={() => setUseCustomTheme(!useCustomTheme)}
                  />
                }
                label={t("Use Custom Theme Colors")}
              />
            </div>

            {useCustomTheme && (
              <div className="d-flex flex-row justify-content-between gap-2">
                <div className="flex-fill mb-2">
                  <Label for="theme">{t("Theme Color 1 ")}</Label>
                  <SketchPicker
                    color={theme1}
                    onChange={(color) => setTheme1(color.hex)}
                  />
                </div>
                <div className="flex-fill mb-2">
                  <Label for="theme">{t("Theme Color 2 ")}</Label>
                  <SketchPicker
                    color={theme2}
                    onChange={(color) => setTheme2(color.hex)}
                  />
                </div>
              </div>
            )}

            <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
              <Permission level={2} feature="space">
                <Button type="submit" color="success" disabled={isLoading}>
                  {isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <span>{t("Add Space")}</span>
                  )}
                </Button>
              </Permission>
              <Button
                color="danger"
                onClick={() => {
                  toggleAddSpaceModal();
                  formik.resetForm();
                  setSaveLastOrder(false); // Reset to false
                  setShowFeedback(true);
                  setSelectedSiteId(siteList[0]?.site_id || null);
                }}
                disabled={isLoading}
              >
                {t("Cancel")}
              </Button>
            </div>
          </form>
        )}
      </ModalBody>
      <ModalFooter />
    </Modal>
  );
};

export default AddSpace;
